// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import theme from '../../styled-components/dark';
import FlagIcon from '../FlagIcon';

const DEFAULT_WRAPPER_PADDING = 3;
const ANIMATION_DURATION = 1800;

const MatchTitleWrapper = styled.div`
  height: 100%;
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  font-size: 105%;
  -webkit-tap-highlight-color: transparent;
  padding: 0 ${({ $pad }) => ($pad ? DEFAULT_WRAPPER_PADDING : 0)}em;
`;

const Title = styled.div`
  color: ${theme.MatchTitle.competitor.color};
  font-weight: bold;
  text-align: center;
`;

const LeftColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: row;
  text-align: right;

  ${({ $shouldAlignOnDash }) => {
    if ($shouldAlignOnDash) {
      return css`
        flex: 20;
      `;
    }
  }}
`;

const CenterColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  text-align: center;
  margin: 0 0.125em;

  padding: 0 0.2em;

  ${({ $shouldAlignOnDash }) => {
    if ($shouldAlignOnDash) {
      return css`
        flex: 1;
      `;
    }
  }};
`;

const RightColumn = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  text-align: left;

  ${({ $shouldAlignOnDash }) => {
    if ($shouldAlignOnDash) {
      return css`
        flex: 20;
      `;
    }
  }}
`;

const Competitor = styled.span`
  font-weight: bold;
  color: ${theme.MatchTitle.competitor.color};
  line-height: 1em;
`;

const ScoreWrapper = styled.div`
  display: flex;

  ${({ $isFromLeft, $paddingLeft, $paddingRight }) => css`
    flex-direction: ${$isFromLeft ? 'row' : 'row-reverse'};
    padding-left: ${$paddingLeft ? '0.3em' : 0};
    padding-right: ${$paddingRight ? '0.3em' : 0};
  `}
`;

const Score = styled.span`
  color: ${({ $color }) => ($color ? $color : theme.MatchTitle.score.color)};
  font-size: 1.2em;
  font-weight: bold;
  padding: 0 0.2em;

  ${({ $isAnimate }) => {
    if ($isAnimate) {
      return css`
        animation: fade-text-in 0.5s;
      `;
    }
  }}

  @keyframes fade-text-in {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledFlagIcon = styled(FlagIcon)`
  width: 1.5em;
  height: 1.5em;
  border-radius: 0.5em;
`;

const Separator = styled.span`
  color: ${theme.MatchTitle.competitor.color};
  margin-bottom: 0.1em;
`;

const CompetitorLeft = styled(Competitor)`
  margin-right: ${({ $addMargin }) => ($addMargin ? '0.3em' : '0.2em')};
`;
const CompetitorRight = styled(Competitor)`
  margin-left: ${({ $addMargin }) => ($addMargin ? '0.3em' : '0.2em')};
`;

const WrapperFlagLeft = styled.div`
  margin-right: 0.2em;
`;

const WrapperFlagRight = styled.div`
  margin-left: 0.2em;
`;

const RedFlag = styled.div`
  width: 1em;
  height: 1.5em;
  background: ${theme.MatchTitle.score.redCardBackgroundColor};
  color: ${theme.MatchTitle.score.redCardColor};

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 60%;
  border-radius: 0.3em;

  ${!Target.__MOBILE__
    ? css`
        padding-bottom: 1px;
      `
    : undefined}

  ${({ $left }) => css`
    transform: rotateZ(${$left ? -15 : 15}deg);
  `}
`;

type PropsType = {
  competitor1Name: string;
  competitor2Name: string;
  competitor1NbRedCards?: number;
  competitor2NbRedCards?: number;
  score1?: number | null;
  score2?: number | null;
  flag1?: string;
  flag2?: string;
  title?: string;
  className?: string;
  darkScore?: boolean;
  noPadding?: boolean;
  animatedScore?: boolean;
  isFinalScore?: boolean;
};

const THRESHOLD = 30;

/**
 * Retourne vrai si la longueur cumulée des 2 competiteurs est supérieur à la limite
 * @returns
 */
const isCompetitorsTitleTooLong = (c1, c2) => (c1?.length ?? 0) + (c2?.length ?? 0) > THRESHOLD;

const MatchTitle = ({
  competitor1Name,
  competitor2Name,
  competitor1NbRedCards = 0,
  competitor2NbRedCards = 0,
  score1,
  score2,
  flag1,
  flag2,
  title,
  className,
  darkScore = false,
  noPadding = false,
  animatedScore = false,
  isFinalScore,
}: PropsType) => {
  const [displayedScore1, setDisplayedScore1] = useState(null);
  const [displayedScore2, setDisplayedScore2] = useState(null);
  const [animeScore, setAnimeScore] = useState(false);
  const wrapperRef = useRef();

  useEffect(() => {
    let timeout = null;

    if (displayedScore1 === null || !animatedScore) {
      setDisplayedScore1(score1);
    }

    if (displayedScore2 === null || !animatedScore) {
      setDisplayedScore2(score2);
    }

    if (!animatedScore || displayedScore1 === null || displayedScore2 === null || score1 === null || score2 === null) {
      return;
    }

    // We delayed the state update so the score is refreshed after the animation ended
    if (score1 > displayedScore1) {
      setAnimeScore(true);
      timeout = setTimeout(() => {
        setDisplayedScore1(score1);
      }, ANIMATION_DURATION);
    } else {
      setDisplayedScore1(score1);
    }

    if (score2 > displayedScore2) {
      setAnimeScore(true);
      timeout = setTimeout(() => {
        setDisplayedScore2(score2);
      }, ANIMATION_DURATION);
    } else {
      setDisplayedScore2(score2);
    }

    setTimeout(() => {
      setAnimeScore(false);
    }, ANIMATION_DURATION + 1000);

    return () => clearTimeout(timeout);
  }, [score1, score2, displayedScore1, displayedScore2, competitor1Name, animatedScore]);

  const shouldDisplayScore = useMemo(
    () => score1 !== undefined && score1 !== null && score2 !== undefined && score2 !== null,
    [score1, score2],
  );

  const shouldDisplayFlag = useMemo(
    () => flag1 !== undefined && flag1 !== null && flag2 !== undefined && flag2 !== null,
    [flag1, flag2],
  );

  const shouldAlignOnDash = useMemo(
    () => shouldDisplayScore || shouldDisplayFlag || isCompetitorsTitleTooLong(competitor1Name, competitor2Name),
    [shouldDisplayScore, shouldDisplayFlag, competitor1Name, competitor2Name],
  );

  const renderScore = useCallback(
    (score, left) => {
      if (shouldDisplayScore) {
        return (
          <ScoreWrapper
            $isFromLeft={left}
            $paddingLeft={left && !!competitor1NbRedCards}
            $paddingRight={!left && !!competitor2NbRedCards}
          >
            {left && !!competitor1NbRedCards ? <RedFlag $left>{competitor1NbRedCards}</RedFlag> : null}
            {!left && !!competitor2NbRedCards ? <RedFlag>{competitor2NbRedCards}</RedFlag> : null}
            <Score
              $isAnimate={animeScore}
              $color={
                isFinalScore
                  ? theme.MatchTitle.score.colorFinalScore
                  : darkScore
                    ? theme.MatchTitle.score.darkColor
                    : null
              }
              key={score}
              $darkScore={darkScore}
            >
              {score}
            </Score>
          </ScoreWrapper>
        );
      }
      return null;
    },
    [shouldDisplayScore, competitor1NbRedCards, competitor2NbRedCards, animeScore, isFinalScore, darkScore],
  );

  const renderFlag = useCallback(
    (flagId, left) => {
      if (shouldDisplayFlag) {
        const WrapperFlag = left ? WrapperFlagLeft : WrapperFlagRight;
        return (
          <WrapperFlag>
            <StyledFlagIcon flagId={flagId} />
          </WrapperFlag>
        );
      }

      return null;
    },
    [shouldDisplayFlag],
  );

  if (title) {
    return (
      <MatchTitleWrapper className={className} $pad={!noPadding}>
        <Title>{title}</Title>
      </MatchTitleWrapper>
    );
  }

  if (competitor1Name && competitor2Name) {
    return (
      <MatchTitleWrapper ref={wrapperRef} className={className} $pad={!noPadding}>
        <LeftColumn $shouldAlignOnDash={shouldAlignOnDash}>
          <CompetitorLeft $addMargin={shouldDisplayScore || shouldDisplayFlag}>{competitor1Name}</CompetitorLeft>
          {renderFlag(flag1, true)}
          {renderScore(displayedScore1, true)}
        </LeftColumn>
        <CenterColumn $shouldAlignOnDash={shouldAlignOnDash}>
          <Separator>-</Separator>
        </CenterColumn>
        <RightColumn $shouldAlignOnDash={shouldAlignOnDash}>
          {renderScore(displayedScore2, false)}
          {renderFlag(flag2, false)}
          <CompetitorRight $addMargin={shouldDisplayScore || shouldDisplayFlag}>{competitor2Name}</CompetitorRight>
        </RightColumn>
      </MatchTitleWrapper>
    );
  }
  return null;
};

export default MatchTitle;
